import React from 'react';
import { observer } from 'mobx-react-lite';
import { Routes, Route, Navigate } from 'react-router-dom';

import FamiliesScreen from '@/screens/family/FamiliesScreen';
import GedcomUploadScreen from '@/screens/upload/GedcomUploadScreen';
import HybridPedigreeScreen from '@/screens/pedigree/HybridPedigreeScreen';
import FamilyCreationScreen from '@/screens/family/FamilyCreationScreen';
import FamilyMembersScreen from '@/screens/family-member/FamilyMembers';
import FamilyMediaScreen from '@/screens/family/FamilyMediaScreen';
import FamilySourcesScreen from '@/screens/family/FamilySourcesScreen';
import FamilyMemoriesScreen from '@/screens/family-memories/FamilyMemoriesScreen';
import MemoryDetailScreen from '@/screens/family-memories/MemoryDetailScreen';
import FamilyOverviewScreen from '@/screens/family/FamilyOverviewScreen';
import FamilyActivityScreen from '@/screens/family/FamilyActivityScreen';
import FamilyReviewQueueScreen from '@/screens/family/FamilyReviewQueueScreen';
import FamilyMySuggestionsScreen from '@/screens/family/FamilyMySuggestionsScreen';
import InviteFamilyMembersScreen from '@/screens/invitations/InviteFamilyMembersScreen';
import AcceptInvitationScreen from '@/screens/invitations/AcceptInvitationScreen';
import ChangelogScreen from '@/screens/changelog/ChangelogScreen';
import HelpScreen from '@/screens/help/HelpScreen';
import ProfileScreen from '@/screens/profile/ProfileScreen';
import DeleteAccountScreen from '@/screens/profile/DeleteAccountScreen';
import PersonProfileScreen from '@/screens/family-member/PersonProfileScreen';
import PetProfileScreen from '@/screens/family-member/PetProfileScreen';
import AddFamilyMemberScreen from '@/screens/family-member/AddFamilyMemberScreen';
import AddPetScreen from '@/screens/family-member/AddPetScreen';
import Layout from '@/components/layout/Layout';

// Default route component - always redirect to families
const DefaultRoute: React.FC = observer(() => {
  return <Navigate to="/families" replace />;
});

// AppWrapper component with routing
const AppWrapper: React.FC = observer(() => {
  return (
    <Routes>
      {/* Public route for accepting invitations */}
      <Route path="/accept-invitation" element={<AcceptInvitationScreen />} />

      {/* Protected routes with layout */}
      <Route path="/" element={<Layout />}>
        <Route index element={<DefaultRoute />} />
        <Route path="families" element={<FamiliesScreen />} />
        <Route path="create-family" element={<FamilyCreationScreen />} />
        <Route path="upload" element={<GedcomUploadScreen />} />
        <Route path="families/:familyId/upload" element={<GedcomUploadScreen />} />
        <Route path="families/:familyId/overview" element={<FamilyOverviewScreen />} />
        <Route path="families/:familyId/activity" element={<FamilyActivityScreen />} />
        <Route path="families/:familyId/review" element={<FamilyReviewQueueScreen />} />
        <Route path="families/:familyId/suggestions" element={<FamilyMySuggestionsScreen />} />
        <Route path="families/:familyId/hybrid-pedigree/:familyMemberId" element={<HybridPedigreeScreen />} />
        <Route path="families/:familyId/members" element={<FamilyMembersScreen />} />
        <Route path="families/:familyId/memories" element={<FamilyMemoriesScreen />} />
        <Route path="families/:familyId/memories/:storyId" element={<MemoryDetailScreen />} />
        <Route path="families/:familyId/media" element={<FamilyMediaScreen />} />
        <Route path="families/:familyId/sources" element={<FamilySourcesScreen />} />
        <Route path="families/:familyId/people/add" element={<AddFamilyMemberScreen />} />
        <Route path="families/:familyId/pets/add" element={<AddPetScreen />} />
        <Route path="families/:familyId/people/:familyMemberId" element={<PersonProfileScreen />} />
        <Route path="families/:familyId/pets/:familyMemberId" element={<PetProfileScreen />} />
        <Route path="families/:familyId/invite" element={<InviteFamilyMembersScreen />} />
        <Route path="changelog" element={<ChangelogScreen />} />
        <Route path="changelog/:version" element={<ChangelogScreen />} />
        <Route path="help" element={<HelpScreen />} />
        <Route path="profile" element={<ProfileScreen />} />
        <Route path="profile/delete-account" element={<DeleteAccountScreen />} />
      </Route>

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/families" replace />} />
    </Routes>
  );
});

export default AppWrapper;
